<script lang="ts" setup>
import { AppButton, AppLogo, AppPassword, AppTextField, AppTypography } from '@/core/components';
import { AppPasswordCriteria } from '@/core/components/fields';
import type { FormErrors } from '@/core/types/Form';
import { useVuelidate } from '@vuelidate/core';
import { ref } from 'vue';
import type { RegisterForm } from '../types/Auth';
import { useAuthStore } from '@/modules/auth/store/auth';
import { useFilterUser } from '@/store/filterUser';

const { resetFilterOptions } = useFilterUser();
const { resetOptions } = useAuthStore();
onMounted(async () => {
  await resetFilterOptions();
  await resetOptions();
});

const { validations } = useValidation();

const form = ref<RegisterForm>({
  email: '',
  password: '',
  password_confirmation: '',
});
const rules = () => ({
  email: {
    required: validations.required('Email'),
    email: validations.email(),
  },
  password: {
    required: validations.required('Password'),
    minLength: validations.minLength('Password', 8),
    password: validations.password(),
  },
  password_confirmation: {
    required: validations.required('Password Confirmation'),
    sameAs: validations.sameAs('Password Confirmation', 'Password', form.value.password),
  },
});

const router = useRouter();

const v$ = useVuelidate(rules, form, { $autoDirty: true });

const { token } = await useGenerateReCaptcha('register');
const { token: loginToken } = await useGenerateReCaptcha('login');

const parsedForm = computed(() => {
  return {
    email: form.value.email,
    password: form.value.password,
    password_confirmation: form.value.password_confirmation,
    recaptcha: token.value,
  };
});

const { data, signUp, signIn } = useAuth();
const registerError = ref<{ data: FormErrors<RegisterForm> }>({ data: { errors: {}, message: '' } });
const registerLoading = ref(false);
const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();
  if (!result) return;
  try {
    registerLoading.value = true;
    await signUp(parsedForm.value, { redirect: false }, { preventLoginFlow: true });
    await signIn(
      { username: parsedForm.value.email, password: parsedForm.value.password, token: loginToken.value },
      { redirect: false }
    );
    if (data.value) {
      router.push({ name: 'verify' });
    }
  } catch (error) {
    registerError.value = error;
  } finally {
    registerLoading.value = false;
  }
};

const errors = computed(() => {
  return parsedError<RegisterForm>(form.value, v$, registerError.value?.data?.errors);
});
</script>

<template>
  <v-card max-width="500" width="100%" variant="plain" elevation="0" class="mx-auto pa-6">
    <v-form @submit.prevent="handleSignUp">
      <AppLogo class="mx-auto my-6" height="100" width="100" />
      <AppTypography type="large" element="h1" class="text-center">SupportApp</AppTypography>
      <AppTypography type="body-2" class="text-center py-6">
        Enter your email address and password to create your account.
      </AppTypography>

      <AppTextField
        v-model="form.email"
        data-cy="input-email"
        :error-messages="errors.email"
        :disabled="registerLoading"
        label="Email address"
        data-testid="email"
        class="mb-1"
      />

      <AppPassword
        v-model="form.password"
        data-cy="input-password"
        label="Password"
        :error-messages="errors.password"
        :disabled="registerLoading"
        data-testid="password"
        class="mb-1"
      />

      <AppPassword
        v-model="form.password_confirmation"
        data-cy="input-password-confirmation"
        label="Confirm password"
        data-testid="password-confirmation"
        :error-messages="errors.password_confirmation"
        :disabled="registerLoading"
        class="mb-1"
      />

      <AppPasswordCriteria :password="form.password" />

      <AppButton
        data-cy="button-submit"
        block
        class="mt-4 mb-8 font-weight-bold"
        type="submit"
        data-testid="submit"
        :disabled="!form.email && !form.password && !form.password_confirmation"
        :loading="registerLoading"
      >
        Continue
      </AppButton>

      <AppTypography element="span" type="subheadline-1" class="d-block text-decoration-none mb-8">
        By signing up, I agree to the
        <nuxt-link to="/terms-of-use" class="text-primary text-decoration-none">Terms of Use</nuxt-link>
        and
        <nuxt-link to="/privacy-policy" class="text-primary text-decoration-none">Privacy Policy</nuxt-link>
      </AppTypography>
      <AppTypography element="span" class="d-block text-center text-decoration-none">
        Already have an account?
        <nuxt-link to="/login" class="text-primary font-weight-bold text-decoration-none">Sign in</nuxt-link>
      </AppTypography>
    </v-form>
  </v-card>
</template>

<style lang="scss" scoped>
.v-card {
  gap: 24px;
  border-radius: 16px;
  background-color: white;

  @media (min-width: 768px) {
    padding: 32px, 16px, 32px, 16px;
  }
}

.v-card-title {
  line-height: unset !important;
  white-space: normal;
}

.v-card-subtitle {
  white-space: normal;
}

.font-footnote {
  font-weight: 500 !important;
  padding-top: 2px;
}
</style>
